import React, { useContext, useEffect, useState } from 'react'
import Feature from 'components/Feature'
import ScreenLoading from 'components/ScreenLoading'
import UpgradeButton from 'components/UpgradeButton'
import useCreateInvite from 'containers/invites/useCreateInvite'
import useDeleteInvite from 'containers/invites/useDeleteInvite'
import useGetPendingInvites from 'containers/invites/useGetPendingInvites'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useGetOrganizationUsers from 'containers/organizations/useGetOrganizationUsers'
import produce from 'immer'

interface MemberData {
  email: string
  uuid?: string
  id?: number | string
  status?: string
}

interface StatusIndicatorProps {
  status: 'loading' | 'pending' | 'member' | 'invited'
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ status }) => {
  const statusConfig = {
    loading: { icon: null, text: 'Loading...', color: '#6c757d' },
    pending: { icon: 'schedule', text: 'Pending', color: '#ffc107' },
    invited: { icon: 'mail_outline', text: 'Invited', color: '#17a2b8' },
    member: { icon: 'check_circle', text: 'Active', color: '#28a745' },
  }

  const config = statusConfig[status]

  if (status === 'loading') {
    return <ScreenLoading />
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: "center" }}>
      {config.icon && (
        <span className="material-icons-outlined" style={{ fontSize: '1.2rem', color: config.color }}>
          {config.icon}
        </span>
      )}
      <span style={{ fontSize: '0.9rem', fontWeight: 500, color: config.color }}>
        {config.text}
      </span>
    </div>
  )
}

interface MemberRowProps {
  member: MemberData
  status: 'pending' | 'member' | 'invited'
  deleteInviteWithOrganization?: (params: { invite: MemberData }) => Promise<boolean>
  handleDeleteInviteFromList?: (uuid: string) => void
}

const MemberRow: React.FC<MemberRowProps> = ({
  member,
  status,
  deleteInviteWithOrganization,
  handleDeleteInviteFromList,
}) => {
  const [hovered, setHovered] = useState(false)
  const [deleteError, setDeleteError] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteInvite = async () => {
    if (!deleteInviteWithOrganization || !handleDeleteInviteFromList) return

    setIsDeleting(true)
    setDeleteError(false)

    try {
      const success = await deleteInviteWithOrganization({ invite: member })
      if (!success) {
        setDeleteError(true)
        return
      }
      handleDeleteInviteFromList(member.uuid!)
    } catch (err) {
      setDeleteError(true)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <td style={{ padding: '1rem', wordBreak: 'break-word', minWidth: '250px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: '#e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              fontWeight: 600,
              color: '#495057',
              flexShrink: 0,
            }}
          >
            {member.email.length > 0 && member.email[0] && member.email[0].toUpperCase()}
          </div>
          <span style={{ fontSize: '0.95rem', wordBreak: 'break-word', overflow: 'hidden' }}>{member.email}</span>
        </div>
      </td>
      <td style={{ padding: '1rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
        <StatusIndicator status={status} />
      </td>
      <td style={{ padding: '1rem', textAlign: 'right', width: '100px' }}>
        {status === 'pending' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem' }}>
            {isDeleting ? (
              <ScreenLoading />
            ) : (
              <>
                {hovered && (
                  <button
                    onClick={handleDeleteInvite}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      color: '#dc3545',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="Cancel invitation"
                  >
                    <span className="material-icons-outlined">delete_outline</span>
                  </button>
                )}
                {deleteError && (
                  <span className="material-icons-outlined" style={{ color: '#dc3545', fontSize: '1.2rem' }}>
                    error
                  </span>
                )}
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}

interface InvitesTableProps {
  pendingInvites: MemberData[]
  organizationUsers: MemberData[]
  deleteInviteWithOrganization: (params: { invite: MemberData }) => Promise<boolean>
  handleDeleteInviteFromList: (uuid: string) => void
}

const InvitesTable: React.FC<InvitesTableProps> = ({
  pendingInvites,
  organizationUsers,
  deleteInviteWithOrganization,
  handleDeleteInviteFromList,
}) => {
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'auto', backgroundColor: '#fff' }}>
      <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.85rem', color: '#495057', minWidth: '250px' }}>
              MEMBER
            </th>
            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#495057', width: '150px' }}>
              STATUS
            </th>
            <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, fontSize: '0.85rem', color: '#495057', width: '100px' }}>
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody>
          {pendingInvites.map((invite) => (
            <MemberRow
              key={invite.uuid}
              member={invite}
              status="pending"
              deleteInviteWithOrganization={deleteInviteWithOrganization}
              handleDeleteInviteFromList={handleDeleteInviteFromList}
            />
          ))}
          {organizationUsers.map((member) => (
            <MemberRow key={member.id} member={member} status="member" />
          ))}
          {pendingInvites.length === 0 && organizationUsers.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: '3rem', textAlign: 'center', color: '#6c757d' }}>
                <span className="material-icons-outlined" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>
                  people_outline
                </span>
                No members yet. Invite someone to get started!
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

interface InviteNewMemberProps {
  handleCreateInvite: (params: { email: string }) => Promise<void>
  existingEmails: string[]
  organizationDomain?: string
}

const InviteNewMember: React.FC<InviteNewMemberProps> = ({
  handleCreateInvite,
  existingEmails,
  organizationDomain
}) => {
  const [newMember, setNewMember] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showSuggestion, setShowSuggestion] = useState(false)

  const emailExists = existingEmails.includes(newMember.toLowerCase())
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember)

  // Check if user is typing and might want domain autocomplete
  const shouldShowAutocomplete = organizationDomain &&
    newMember.includes('@') &&
    !newMember.endsWith(organizationDomain) &&
    newMember.split('@')[1] &&
    organizationDomain.startsWith(newMember.split('@')[1])

  const handleSubmit = async () => {
    setError('')

    if (!newMember.trim()) {
      setError('Please enter an email address')
      return
    }

    if (!isValidEmail) {
      setError('Please enter a valid email address')
      return
    }

    if (emailExists) {
      setError('This email has already been invited or is already a member')
      return
    }

    setLoading(true)
    try {
      await handleCreateInvite({ email: newMember })
      setNewMember('')
      setError('')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleSubmit()
    } else if (event.key === 'Tab' && shouldShowAutocomplete) {
      event.preventDefault()
      const username = newMember.split('@')[0]
      setNewMember(`${username}@${organizationDomain}`)
      setShowSuggestion(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (loading) return
    setNewMember(e.target.value)
    setError('')
    setShowSuggestion(false)
  }

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div
        style={{
          padding: '1.5rem',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fff',
        }}
      >
        <h4 style={{ margin: '0 0 1rem 0' }}>Invite New Member</h4>
        <div style={{ position: 'relative' }}>
          <div className="new-member-form" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px', position: 'relative' }}>
              <input
                type="email"
                value={newMember}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter email address..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${error ? '#dc3545' : emailExists ? '#ffc107' : '#e0e0e0'}`,
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                }}
                disabled={loading}
              />
              {shouldShowAutocomplete && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#6c757d',
                    zIndex: 10,
                  }}
                >
                  Press <kbd style={{ padding: '0.125rem 0.25rem', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '3px' }}>Tab</kbd> to autocomplete with @{organizationDomain}
                </div>
              )}
            </div>
            <button
              onClick={handleSubmit}
              className="submit-button"
              disabled={loading || !newMember.trim() || !isValidEmail || emailExists}
              style={{ minWidth: '120px', padding: '0.75rem 1.5rem', flex: '0 0 auto' }}
            >
              {loading ? <ScreenLoading spinnerColor="white" /> : 'Send Invite'}
            </button>
          </div>
          {error && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#dc3545' }}>
              {error}
            </p>
          )}
          {emailExists && !error && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#856404' }}>
              This user is already invited or is a member
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default function Invites() {
  const {
    pendingInvites,
    setPendingInvites,
    loadingPendingInvites,
    pendingInvitesError,
    getPendingInvites,
    handleDeleteInviteFromList,
  } = useGetPendingInvites()

  const {
    organizationUsers,
    loadingOrganizationUsers,
    organizationUsersError,
    getOrganizationUsers,
  } = useGetOrganizationUsers()

  const { createInvite } = useCreateInvite()
  const { deleteInvite } = useDeleteInvite()
  const { currentOrganization, isLoadingOrganization } = useContext(OrganizationsContext)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'member'>('all')

  const getData = async () => {
    if (!isLoadingOrganization) {
      getPendingInvites({ organization: currentOrganization })
      getOrganizationUsers({ organization: currentOrganization })
    }
  }

  useEffect(() => {
    getData()
    // eslint-disable-next-line
  }, [isLoadingOrganization, currentOrganization])

  if (pendingInvitesError || organizationUsersError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc3545' }}>
        <span className="material-icons-outlined" style={{ fontSize: '3rem', display: 'block', marginBottom: '1rem' }}>
          error_outline
        </span>
        Failed to load members. Please try again.
      </div>
    )
  }

  const deleteInviteWithOrganization = async ({ invite }: { invite: MemberData }) => {
    return await deleteInvite({ organization: currentOrganization, invite })
  }

  const handleCreateInvite = async ({ email }: { email: string }) => {
    const newInvite = await createInvite({
      email,
      organization: currentOrganization,
    })

    if (!newInvite) {
      throw new Error('Failed to create invitation')
    }

    setPendingInvites(
      produce(pendingInvites, (draft) => {
        draft.unshift({
          ...newInvite,
          status: 'pending',
        })
      })
    )
  }

  // Get all existing emails (both members and pending invites)
  const existingEmails = [
    ...(pendingInvites || []).map(inv => inv.email.toLowerCase()),
    ...(organizationUsers || []).map(user => user.email.toLowerCase())
  ]

  // Extract organization domain from current users
  const organizationDomain = organizationUsers && organizationUsers.length > 0
    ? organizationUsers[0].email.split('@')[1]
    : undefined

  // Filter members based on search and status
  const filteredPendingInvites = (pendingInvites || []).filter(invite => {
    const matchesSearch = invite.email.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || statusFilter === 'pending'
    return matchesSearch && matchesStatus
  })

  const filteredOrganizationUsers = (organizationUsers || []).filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || statusFilter === 'member'
    return matchesSearch && matchesStatus
  })

  const totalFilteredCount = filteredPendingInvites.length + filteredOrganizationUsers.length

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '900px', padding: '0 1rem', boxSizing: 'border-box' }}>
      {/* Explanatory Notice */}
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          borderRadius: '4px',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span className="material-icons-outlined" style={{ color: '#0c5460' }}>
            info
          </span>
          <strong style={{ color: '#0c5460' }}>About Team Members</strong>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', color: '#0c5460', fontSize: '0.9rem', lineHeight: '1.6' }}>
          Invite team members to collaborate on your organization. Invited members will receive an email with instructions to join.
        </p>
        <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#0c5460' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem'}}>
            <span className="material-icons-outlined" style={{ fontSize: '1rem', color: '#ffc107' }}>schedule</span>
            <strong>Pending:</strong> Invitation sent, awaiting acceptance
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
            <span className="material-icons-outlined" style={{ fontSize: '1rem', color: '#28a745' }}>check_circle</span>
            <strong>Active:</strong> User has joined and has full access
          </div>
        </div>
      </div>

      {isLoadingOrganization || loadingPendingInvites || loadingOrganizationUsers ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <ScreenLoading />
        </div>
      ) : (
        <>
          <Feature
            feature="can_add_users"
            switchComponent={
              <UpgradeButton style={{ marginBottom: 20 }}>
                Upgrade plan to add more users
              </UpgradeButton>
            }
          >
            <InviteNewMember
              handleCreateInvite={handleCreateInvite}
              existingEmails={existingEmails}
              organizationDomain={organizationDomain}
            />
          </Feature>

          {/* Search and Filter Section */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: '1 1 300px', position: 'relative' }}>
              <span className="material-icons-outlined" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368', fontSize: '20px' }}>
                search
              </span>
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 0.75rem 0.75rem 2.75rem',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s',
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#5f6368',
                  }}
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>close</span>
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setStatusFilter('all')}
                style={{
                  padding: '0.75rem 1.25rem',
                  border: statusFilter === 'all' ? '2px solid #739570' : '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === 'all' ? '#f0f7ef' : '#fff',
                  color: statusFilter === 'all' ? '#739570' : '#495057',
                  fontWeight: statusFilter === 'all' ? 600 : 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                All ({(pendingInvites?.length || 0) + (organizationUsers?.length || 0)})
              </button>
              <button
                onClick={() => setStatusFilter('pending')}
                style={{
                  padding: '0.75rem 1.25rem',
                  border: statusFilter === 'pending' ? '2px solid #ffc107' : '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === 'pending' ? '#fff9e6' : '#fff',
                  color: statusFilter === 'pending' ? '#856404' : '#495057',
                  fontWeight: statusFilter === 'pending' ? 600 : 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Pending ({pendingInvites?.length || 0})
              </button>
              <button
                onClick={() => setStatusFilter('member')}
                style={{
                  padding: '0.75rem 1.25rem',
                  border: statusFilter === 'member' ? '2px solid #28a745' : '1px solid #e0e0e0',
                  borderRadius: '8px',
                  backgroundColor: statusFilter === 'member' ? '#e6f4ea' : '#fff',
                  color: statusFilter === 'member' ? '#155724' : '#495057',
                  fontWeight: statusFilter === 'member' ? 600 : 500,
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Active ({organizationUsers?.length || 0})
              </button>
            </div>
          </div>

          {/* Results count */}
          {(searchQuery || statusFilter !== 'all') && (
            <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#5f6368' }}>
              Showing {totalFilteredCount} of {(pendingInvites?.length || 0) + (organizationUsers?.length || 0)} members
            </div>
          )}

          <InvitesTable
            pendingInvites={filteredPendingInvites}
            organizationUsers={filteredOrganizationUsers}
            deleteInviteWithOrganization={deleteInviteWithOrganization}
            handleDeleteInviteFromList={handleDeleteInviteFromList}
          />
        </>
      )}
    </div>
  )
}
